import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { AgGridReact } from 'ag-grid-react';
import { PlacementDetailsTableService, PlacementUtils } from "../../../../placements";
import { InlineEditDialogBox, OmniIconComponent, OmniTooltipNextComponent, Utils } from "../../../../../shared";
import CampaignDetailsActionMenuRenderer from "./campaignDetailsActionMenuRenderer";

// Constants
const defaultColDef = {
    flex: 5,
    filter: false,
    resizable: true,
    wrapText: true,
    autoHeight: true,
    floatingFilter: false,
    suppressHeaderMenuButton: true,
};

const mediaOrdersColumnDefs = [
    {
        headerName: "Channel",
        field: "channelDisplayNameAggrid",
        resizable: true,
        wrapText: true,
        autoHeight: true,
        flex: 1.5,
        filter: false,
        floatingFilter: false,
        suppressFloatingFilterButton: false,
        sort: 'asc'
    },
    {
        headerName: "Supplier",
        field: "supplierDisplayNameAggrid",
        resizable: true,
        wrapText: true,
        autoHeight: true,
        flex: 1.5,
        filter: false,
        floatingFilter: false,
        suppressFloatingFilterButton: false,
    },
    {
        headerName: "Date Range",
        field: "dateRangeDisplayNameAggrid",
        resizable: true,
        wrapText: true,
        autoHeight: true,
        flex: 1.5,
        filter: false,
        floatingFilter: false,
        suppressFloatingFilterButton: false,
    },
    {
        headerName: "Ordered",
        field: "orderedDisplayNameAggrid",
        resizable: true,
        wrapText: true,
        autoHeight: true,
        flex: 1,
        filter: false,
        floatingFilter: false,
        suppressFloatingFilterButton: false,
        cellStyle: { textAlign: 'right' }
    },
    {
        headerName: "Actions",
        field: "actionsDisplayNameAggrid",
        resizable: true,
        wrapText: true,
        autoHeight: true,
        flex: 1.6,
        filter: false,
        floatingFilter: false,
        suppressFloatingFilterButton: false,
    }
];

const MediaOrdersTable = (params) => {
    // Refs
    const gridRef = useRef(null);
    const isInlineOverrideRef = useRef(false);

    // State
    const [openActionMenuId, setOpenActionMenuId] = useState(null);
    const [data, setData] = useState([]);
    const [highlightedRowId, setHighlightedRowId] = useState(null);
    const [columnDefs, setColumnDefs] = useState([]);
    const [taxonomyName, setTaxonomyName] = useState(null);
    const [selectedCampaignObj, setSelectedCampaignObj] = useState(null);
    const [isShowModal, setIsShowModal] = useState(false);
    const [taxonomyKeys, setTaxonomyKeys] = useState(null);
    const [taxonomyNameWithAbbrevation, setTaxonomyNameWithAbbrevation] = useState(null);
    const [taxonomySchema, setTaxonomySchema] = useState(null);
    const [editableData, setEditableData] = useState(null);
    const [editFormData, setEditFormData] = useState(null);
    const [taxonomySchemaArray, setTaxonomySchemaArray] = useState(null);

    // Cell Renderers
    const dateRangeRenderer = useCallback((params) => {
        if (!params.data) {
            return '-';
        }

        if (params.data.dataTypeForAggrid === 'publisher') {
            const startDate = params.data.publisher_start_date;
            const endDate = params.data.publisher_end_date;
            return `${Utils.formatDateObject(startDate)} - ${Utils.formatDateObject(endDate)}`;
        } else if (params.data.dataTypeForAggrid === 'campaign') {
            const startDate = params.data.start_date;
            const endDate = params.data.end_date;
            return `${Utils.formatDateObject(startDate)} - ${Utils.formatDateObject(endDate)}`;
        }
    }, []);
    
    const orderedDisplayNameAggridRenderer = useCallback((rendererParams) => {
        if (!rendererParams.data) {
            return '-';
        }

        if (rendererParams.data?.orderedDisplayNameAggrid || rendererParams.data?.orderedDisplayNameAggridLocal) {
            const isLocal = params?.systemParametersData?.data?.isLocal;
            if (isLocal) {
                const displayValue = Utils.getLocalCurrency(rendererParams?.data?.local_currency_name ? rendererParams?.data?.local_currency_name : 'USD', Number(rendererParams?.data?.orderedDisplayNameAggridLocal || 0))
                return (<span className="ordered-amount">{displayValue}</span>)
            } else {
                const displayValue = Utils.getLocalCurrency('USD', Number(rendererParams?.data?.orderedDisplayNameAggrid || 0))
                return (<span className="ordered-amount">{displayValue}</span>)
            }
        }

        return '-';

    }, []);

    const channelRenderer = useCallback((rendererParams) => {
        if (rendererParams.data?.dataTypeForAggrid === 'total') {
            return '';
        }

        if (!rendererParams.data) {
            return '-';
        }

        if (rendererParams.data.dataTypeForAggrid === 'campaign') {
            const channelNames = rendererParams.data?.channelPublisherNameArray;

            if (!channelNames) {
                return '';
            }

            const channels = channelNames.split(',').map(name => name.trim()).filter(Boolean); // Split by comma and trim whitespace

            if (channels.length === 0) {
                return '';
            }

            const visibleChannels = channels.slice(0, 2); // Show first 2 channels as pills
            const remainingCount = channels.length - 2;

            return (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {visibleChannels.map((channel, index) => (
                        <span key={index} className="ad-serving-fee-pill-container">
                            <span className="ad-serving-fee-pill">
                                {channel}
                            </span>
                        </span>
                    ))}
                    {remainingCount > 0 && (
                        <span style={{ color: '#006A98 !important', fontWeight: '600 !important' }}>
                            +{remainingCount}
                        </span>
                    )}
                </div>
            );
        } else {
            return (
                <span className="ad-serving-fee-pill-container">
                    <span className="ad-serving-fee-pill">{rendererParams.data?.channelDisplayNameAggrid}</span>
                </span>
            )
        }

    }, []);

    const toggleActionMenu = useCallback((id) => {
        setOpenActionMenuId(prevId => {
            return prevId === id ? null : id;
        });
    }, []);

    // Refresh cells when menu state changes
    useEffect(() => {
        if (gridRef?.current?.api) {
            gridRef.current.api.refreshCells({
                columns: ['actionsDisplayNameAggrid'],
                force: true
            });
        }
    }, [openActionMenuId]);

    const genrateCampaignTaxonomyName = useCallback(() => {
        let campaignTaxonomyName = "", campaignTaxonomyNameWithAbbrevation = "", taxonomySchema = "", taxonomyJson = [];
        let campaignObj = selectedCampaignObj;

        if (!campaignObj || !params.campaignTaxonomyData?.fields) {
            return;
        }

        params.campaignTaxonomyData.fields.forEach((field, index) => {
            let taxonomyField = {
                code: "NU",
                delimiter: field['delimiter_value'],
                id: field['id'] || null
            }
            if (field?.name?.toLowerCase() === 'channel') {
                const channels = campaignObj['channels'] || [];
                if (channels.length > 1) {
                    campaignTaxonomyName += 'MUL' + field['delimiter_value'];
                    campaignTaxonomyNameWithAbbrevation += 'MUL' + field['delimiter_value'];
                    taxonomyField.code = 'MUL';
                } else if (channels.length === 1) {
                    campaignTaxonomyName += channels[0]['channel_name'] || "NU";
                    campaignTaxonomyName += field['delimiter_value'];
                    campaignTaxonomyNameWithAbbrevation += channels[0]['channel_name'] || " ";
                    campaignTaxonomyNameWithAbbrevation += field['delimiter_value'];
                    taxonomyField.code = channels[0]['channel_name'] || "NU";
                } else {
                    campaignTaxonomyName += "NU" + field['delimiter_value'];
                    campaignTaxonomyNameWithAbbrevation += " " + field['delimiter_value'];
                }
                taxonomySchema += "Channel" + '|';
            } else {
                /* field_type =1 for Dropdown field
                  field_type=2 for FreeText field
                  field_type= 9 for Static Text
                */

                switch (field.field_type) {
                    case 1:
                        let fieldCodeValue, fieldName = "";
                        fieldCodeValue = field.fielditems.filter(fielditem => fielditem['name'] === campaignObj[field?.name?.toLowerCase()]).map((fielditem) => {
                            fieldName = fielditem.name || "";
                            return fielditem.code;
                        })
                        campaignTaxonomyName += (fieldCodeValue[0] || "NU") + field['delimiter_value']
                        campaignTaxonomyNameWithAbbrevation += fieldName + field['delimiter_value']
                        taxonomyField.code = fieldCodeValue[0] || 'NU';
                        break;
                    case 2:
                        campaignTaxonomyName += (campaignObj[field?.name?.toLowerCase()] || "NU") + field['delimiter_value']
                        campaignTaxonomyNameWithAbbrevation += campaignObj[field?.name?.toLowerCase()] + field['delimiter_value']
                        taxonomyField.code = campaignObj[field?.name?.toLowerCase()] || "NU";
                        break;
                    case 9:
                        campaignTaxonomyName += field.static_field_value + field['delimiter_value']
                        campaignTaxonomyNameWithAbbrevation += field.static_field_value + field['delimiter_value']
                        taxonomyField.code = field.static_field_value;
                        break;
                    case 10:
                        campaignTaxonomyName += (campaignObj[field?.name?.toLowerCase()] || "{TaxonomyValueID}") + field['delimiter_value']
                        campaignTaxonomyNameWithAbbrevation += campaignObj[field?.name?.toLowerCase()] + field['delimiter_value']
                        taxonomyField.code = campaignObj[field?.name?.toLowerCase()] || "{TaxonomyValueID}";
                        break;
                    default:
                        campaignTaxonomyName += (campaignObj[field?.name?.toLowerCase()] || "NU") + field['delimiter_value']
                        campaignTaxonomyNameWithAbbrevation += campaignObj[field?.name?.toLowerCase()] + field['delimiter_value']
                        taxonomyField.code = campaignObj[field?.name?.toLowerCase()] || "NU";
                        break;
                }
                taxonomySchema += field["name"] + '|';
            }

            taxonomyJson.push(taxonomyField);
            if (index === params.campaignTaxonomyData.fields.length - 1) {
                setTaxonomyName(campaignTaxonomyName.slice(0, -1));
                setTaxonomyNameWithAbbrevation(campaignTaxonomyNameWithAbbrevation.slice(0, -1));
                setTaxonomySchema(taxonomySchema.slice(0, -1));
            }
        });
    }, [selectedCampaignObj, params.campaignTaxonomyData]);

    const handleDescriptionClick = useCallback((data) => {
        setSelectedCampaignObj(data);
        if (data?.campaignTaxonomy?.split(':|')[0] !== "-" && data?.campaignTaxonomy?.split(':|')[0]) {
            setTaxonomyName(data?.campaignTaxonomy?.split(':|')[0]);
        } else {
            genrateCampaignTaxonomyName();
        }
    }, [genrateCampaignTaxonomyName]);

    const actionsDisplayNameAggridRenderer = useCallback((props) => {
        if (props.data.dataTypeForAggrid !== 'campaign') {
            return null;
        }

        // Get openActionMenuId from context instead of closure
        const isThisOpen = props.context?.openActionMenuId === props?.data?.id;

        const navigateToPlacementDetails = () => {

            let state = { ...params.locationState, campaignObj: props.data };
            params.navigate(`/placementDetailsTable/${props.data?.id}`, { state });
        }

        let statusIconClass = '';
        if (props.data?.export_status === 'Campaign Sent') {
            statusIconClass = 'omni:informative:connectionSuccess';
        } else if (props.data?.export_status?.toLowerCase()?.includes('error')) {
            statusIconClass = 'omni:informative:connectionError';
        } else if (props.data?.export_status === 'Not Approved') {
            statusIconClass = 'omni:informative:connectionNone';
        }

        return (
            <div className="action-buttons">

                <OmniTooltipNextComponent>
                    <OmniIconComponent className="is-size-2 actions-icon" icon-id={statusIconClass}></OmniIconComponent>
                    <div slot="content"> {props.data?.export_status} </div>
                </OmniTooltipNextComponent>

                <button className="secondary" onClick={navigateToPlacementDetails}>Placements</button>
                <CampaignDetailsActionMenuRenderer
                    {...props}
                    id={props?.node?.id + "actions"}
                    isOpen={isThisOpen}
                    onToggle={props.context?.toggleActionMenu}
                    campaign={props?.data}
                    handleDescriptionClick={handleDescriptionClick}
                />
            </div>
        );
    }, [handleDescriptionClick]);

    const applyRenderers = useCallback((cols) => {
        const cellRenderers = {
            dateRangeDisplayNameAggrid: dateRangeRenderer,
            channelDisplayNameAggrid: channelRenderer,
            actionsDisplayNameAggrid: actionsDisplayNameAggridRenderer,
            orderedDisplayNameAggrid: orderedDisplayNameAggridRenderer,
        };

        const applyRenderersRecursive = (columns) => {
            return columns.map(col => {
                // if column has children → recurse
                if (col.children) {
                    return {
                        ...col,
                        children: applyRenderersRecursive(col.children),
                    };
                }

                // otherwise apply renderer if matched
                return cellRenderers[col.field]
                    ? { ...col, cellRenderer: cellRenderers[col.field] }
                    : col;
            });
        };

        return applyRenderersRecursive(cols);
    }, [dateRangeRenderer, channelRenderer, actionsDisplayNameAggridRenderer]);

    useEffect(() => {
        let campaign = selectedCampaignObj;

        if (campaign) {
            let campaignTaxonomy = campaign.campaign_taxonomy.split(':|');
            let taxonomyJson = campaign?.taxonomy ? campaign?.taxonomy : null;
            let taxonomySchema = '';
            let taxonomySchemaArray = params.campaignTaxonomyData;
            let taxonomyData = taxonomySchemaArray?.fields;
            //if campaign is too old and there is taxonomy_ids_schema
            if (!taxonomyJson?.length && campaign?.taxonomy_ids_schema) {
                let taxonomyIds = campaign?.taxonomy_ids_schema.split('|').map(val => {
                    return val.split(' *')[1]
                });

                let newSchema = [];
                taxonomyIds.forEach((id, index) => {
                    let field = {};
                    field = taxonomyData.find(field => id == field.id)
                    let name = field?.name || field?.alias;
                    newSchema.push(name);
                });

                taxonomySchema = newSchema.join('|');
                campaign.taxonomy_schema = taxonomySchema;
            }
            let editFormData = getCampaignEditData();
            let isNameAndSchemaFieldMatch = PlacementDetailsTableService.compareTaxonomyAndData(params.campaignTaxonomyData?.fields, editFormData, campaign?.taxonomy_schema, taxonomyJson);
            if (!isNameAndSchemaFieldMatch) {
                isInlineOverrideRef.current = true;
            }
            campaign.syntheticId = campaign?.synthetic_id;
            const taxonomyKeys = {
                'schema': 'taxonomy_schema',
                'displayName': 'campaign_taxonomy_display',
                'taxonomyName': 'campaign_taxonomy',
                'syntheticId': campaignTaxonomy[1],
            }

            setTaxonomyKeys(taxonomyKeys);
            setEditableData(campaign);
            setEditFormData(editFormData);
            setTaxonomySchemaArray(params.campaignTaxonomyData);
            setIsShowModal(true);
        }

    }, [taxonomyName, selectedCampaignObj]);

    const getCampaignEditData = () => {
        let taxonomySchemaArray = params.campaignTaxonomyData;
        let campaign = selectedCampaignObj;
        let taxonomyJson = campaign?.taxonomy ? campaign?.taxonomy : null;
        let campaignTaxonomy = campaign.campaign_taxonomy.split(':|');
        let taxonomyName = campaignTaxonomy[0];
        let taxonomySchema = campaign?.taxonomy_schema ? campaign?.taxonomy_schema : null;
        let taxonomyData = taxonomySchemaArray?.fields;

        return PlacementDetailsTableService.splitPlacementStringIntoSchemaObject(taxonomyName, taxonomySchema, taxonomyData, '', null, taxonomyJson);
    }

    const closeModal = (payload) => {
        if (Object.keys(payload || {}).length > 0) {
            params.updateSavedScrollPositionRef({ y: window.scrollY });
            params.fetchCampaignDetails();
        }

        setIsShowModal(false);
        setSelectedCampaignObj(null);
        setTaxonomyName(null);
        setTaxonomyNameWithAbbrevation(null);
        setTaxonomySchema(null);
        isInlineOverrideRef.current = false;
        setTaxonomyKeys(null);
        setEditableData(null);
        setEditFormData(null);
        setTaxonomySchemaArray(null);
    }

    // Initialize column definitions once on mount
    useEffect(() => {
        setColumnDefs(applyRenderers(mediaOrdersColumnDefs));
    }, [applyRenderers]);

    /**
     * Processes planning publishers for a channel and matches them with ordered data
     * @param {Object} channel - Channel object containing publishers
     * @param {Object} campaignOrderedData - Ordered data for the campaign
     * @param {Object} campaign - Campaign object
     * @param {Set} matchedOrderedSuppliers - Set to track matched ordered suppliers
     * @param {Set} matchedOrderedChannels - Set to track matched ordered channels
     * @returns {Array} Array of processed publisher objects
     */
    const processPlanningPublishers = useCallback((channel, campaignOrderedData, campaign, matchedOrderedSuppliers, matchedOrderedChannels) => {
        const publishers = [];
        const channelPublishers = channel?.publishers || [];

        for (let publisherIndex = 0; publisherIndex < channelPublishers.length; publisherIndex++) {
            const publisher = { ...channelPublishers[publisherIndex] };
            publisher.dataTypeForAggrid = 'publisher';
            publisher.channelDisplayNameAggrid = channel?.channel_name;
            publisher.supplierDisplayNameAggrid = publisher?.publisher_name || '';

            // Match with ordered data if available
            if (campaignOrderedData) {
                const channelOrderedData = campaignOrderedData.child_campaign_details?.find(
                    item => item?.channel?.toLowerCase() === channel.channel_name?.toLowerCase()
                );

                if (channelOrderedData) {
                    matchedOrderedChannels.add(channel.channel_name.toLowerCase());

                    const publisherOrderedData = channelOrderedData.suppliers?.find(
                        item => item?.supplier?.toLowerCase() === publisher.publisher_name?.toLowerCase()
                    );

                    if (publisherOrderedData) {
                        publisher.orderedDisplayNameAggrid = publisherOrderedData.ordered_total;
                        publisher.orderedDisplayNameAggridLocal = publisherOrderedData.ordered_total_local;
                        publisher.local_currency_name = campaign.local_currency_name;

                        const supplierKey = `${channel.channel_name}|${publisherOrderedData.supplier}`.toLowerCase();
                        matchedOrderedSuppliers.add(supplierKey);
                    }
                }
            }

            publishers.push(publisher);
        }

        return publishers;
    }, []);

    /**
     * Adds missing ordered suppliers within an existing planning channel
     * @param {Object} channel - Channel object
     * @param {Object} campaignOrderedData - Ordered data for the campaign
     * @param {Object} campaign - Campaign object
     * @param {Set} matchedOrderedSuppliers - Set of already matched supplier keys
     * @returns {Array} Array of missing supplier objects
     */
    const addMissingOrderedSuppliersInChannel = useCallback((channel, campaignOrderedData, campaign, matchedOrderedSuppliers) => {
        const missingSuppliers = [];

        const channelOrderedData = campaignOrderedData.child_campaign_details?.find(
            item => item?.channel?.toLowerCase() === channel.channel_name?.toLowerCase()
        );

        if (channelOrderedData?.suppliers) {
            for (const orderedSupplier of channelOrderedData.suppliers) {
                const supplierKey = `${channel.channel_name}|${orderedSupplier.supplier}`.toLowerCase();

                if (!matchedOrderedSuppliers.has(supplierKey)) {
                    const missingPublisher = {
                        channelDisplayNameAggrid: channel.channel_name,
                        supplierDisplayNameAggrid: orderedSupplier.supplier || '',
                        dateRangeDisplayNameAggrid: '',
                        local_currency_name: campaign.local_currency_name,
                        orderedDisplayNameAggrid: orderedSupplier.ordered_total || '',
                        orderedDisplayNameAggridLocal: orderedSupplier.ordered_total_local || '',
                        dataTypeForAggrid: 'publisher',
                    };

                    missingSuppliers.push(missingPublisher);
                    matchedOrderedSuppliers.add(supplierKey);
                }
            }
        }

        return missingSuppliers;
    }, []);

    /**
     * Adds missing ordered channels (channels that exist in ordered data but not in planning)
     * @param {Object} campaignOrderedData - Ordered data for the campaign
     * @param {Object} campaign - Campaign object
     * @param {Set} matchedOrderedChannels - Set of already matched channel names
     * @param {Set} matchedOrderedSuppliers - Set to track matched supplier keys
     * @returns {Array} Array of missing channel supplier objects
     */
    const addMissingOrderedChannels = useCallback((campaignOrderedData, campaign, matchedOrderedChannels, matchedOrderedSuppliers) => {
        const missingChannelSuppliers = [];

        if (!campaignOrderedData.child_campaign_details?.length) {
            return missingChannelSuppliers;
        }

        const missingChannels = campaignOrderedData.child_campaign_details.filter(
            item => !matchedOrderedChannels.has(item.channel?.toLowerCase())
        );

        for (const missingChannel of missingChannels) {
            if (missingChannel?.suppliers) {
                for (const orderedSupplier of missingChannel.suppliers) {
                    const missingPublisher = {
                        channelDisplayNameAggrid: missingChannel.channel || '',
                        supplierDisplayNameAggrid: orderedSupplier.supplier || '',
                        dateRangeDisplayNameAggrid: '',
                        local_currency_name: campaign.local_currency_name,
                        orderedDisplayNameAggrid: orderedSupplier.ordered_total || '',
                        orderedDisplayNameAggridLocal: orderedSupplier.ordered_total_local || '',
                        dataTypeForAggrid: 'publisher',
                    };

                    missingChannelSuppliers.push(missingPublisher);

                    const supplierKey = `${missingChannel.channel}|${orderedSupplier.supplier}`.toLowerCase();
                    matchedOrderedSuppliers.add(supplierKey);
                }
            }
        }

        return missingChannelSuppliers;
    }, []);

    /**
     * Aggregates ordered totals from nested publisher data to campaign level
     * @param {Object} campaign - Campaign object with nestedData array
     * @returns {Object} Campaign with aggregated ordered totals
     */
    const updateCampaignOrderedData = useCallback((campaign) => {
        if (!campaign?.nestedData?.length) {
            return campaign;
        }

        let totalOrdered = 0;
        let totalOrderedLocal = 0;

        campaign.nestedData.forEach(publisher => {
            totalOrdered += Number(publisher?.orderedDisplayNameAggrid || 0);
            totalOrderedLocal += Number(publisher?.orderedDisplayNameAggridLocal || 0);
        });

        campaign.orderedDisplayNameAggrid = totalOrdered;
        campaign.orderedDisplayNameAggridLocal = totalOrderedLocal;

        return campaign;
    }, []);

    /**
     * Processes a single campaign to build nested data structure
     * @param {Object} campaign - Campaign object to process
     * @param {Object} orderedData - Ordered data containing child campaigns
     * @returns {Object} Processed campaign with nested data
     */
    const processCampaign = useCallback((campaign, orderedData) => {
        const processedCampaign = { ...campaign };

        const campaignOrderedData = orderedData?.child_campaigns?.find(
            item => item.child_campaign_id === campaign.synthetic_id
        );

        const channels = campaign?.channels || [];
        processedCampaign.prismaCampaign = campaign.parent_id
            ? campaign.campaign_taxonomy
            : campaign.campaign_display_name;
        processedCampaign.dataTypeForAggrid = 'campaign';

        const nestedData = [];
        let channelPublisherNameArray = '';

        // Track matched items to prevent duplicates
        const matchedOrderedSuppliers = new Set();
        const matchedOrderedChannels = new Set();

        // Process each channel
        for (let channelIndex = 0; channelIndex < channels.length; channelIndex++) {
            const channel = { ...channels[channelIndex] };
            channel.dataTypeForAggrid = 'channel';

            // Add planning publishers with their matched ordered data
            const planningPublishers = processPlanningPublishers(
                channel,
                campaignOrderedData,
                campaign,
                matchedOrderedSuppliers,
                matchedOrderedChannels
            );

            nestedData.push(...planningPublishers);

            // Build channel name list for display
            if (channel?.channel_name) {
                channelPublisherNameArray += `${channel.channel_name}, `;
            }

            // Add ordered suppliers that don't exist in planning for this channel
            if (campaignOrderedData) {
                const missingSuppliers = addMissingOrderedSuppliersInChannel(
                    channel,
                    campaignOrderedData,
                    campaign,
                    matchedOrderedSuppliers
                );
                nestedData.push(...missingSuppliers);
            }
        }

        // Add completely missing channels (exist in ordered but not in planning)
        if (campaignOrderedData) {
            const missingChannelSuppliers = addMissingOrderedChannels(
                campaignOrderedData,
                campaign,
                matchedOrderedChannels,
                matchedOrderedSuppliers
            );
            nestedData.push(...missingChannelSuppliers);

            // Add missing channel names to channelPublisherNameArray
            const addedChannels = new Set();
            missingChannelSuppliers.forEach(supplier => {
                const channelName = supplier.channelDisplayNameAggrid;
                if (channelName && !addedChannels.has(channelName)) {
                    channelPublisherNameArray += `${channelName}, `;
                    addedChannels.add(channelName);
                }
            });
        }

        processedCampaign.nestedData = nestedData;
        processedCampaign.channelPublisherNameArray = channelPublisherNameArray ? channelPublisherNameArray.slice(0, -2) : ''; // Remove trailing comma and space  

        return updateCampaignOrderedData(processedCampaign);
    }, [processPlanningPublishers, addMissingOrderedSuppliersInChannel, addMissingOrderedChannels, updateCampaignOrderedData]);

    /**
     * Main update function - builds complete data structure for the table
     */
    const updateData = useCallback(() => {
        const childCampaigns = params?.childCampaigns || [];

        if (!childCampaigns.length) {
            setData([]);
            return;
        }

        const processedData = childCampaigns.map(campaign =>
            processCampaign(campaign, params.orderedData)
        );

        // Calculate totals across all campaigns
        let totalOrdered = 0;
        let totalOrderedLocal = 0;

        processedData.forEach(campaign => {
            totalOrdered += Number(campaign?.orderedDisplayNameAggrid || 0);
            totalOrderedLocal += Number(campaign?.orderedDisplayNameAggridLocal || 0);
        });

        // Add total row at the bottom
        const totalRow = {
            id: 'total-row',
            prismaCampaign: '',
            channelDisplayNameAggrid: '',
            supplierDisplayNameAggrid: '',
            dateRangeDisplayNameAggrid: '',
            orderedDisplayNameAggrid: totalOrdered,
            orderedDisplayNameAggridLocal: totalOrderedLocal,
            local_currency_name: processedData.length > 0 ? processedData[0]?.local_currency_name : 'USD',
            dataTypeForAggrid: 'total',
            nestedData: [],
        };

        setData([...processedData, totalRow]);
    }, [params?.childCampaigns, params.orderedData, processCampaign]);


    useEffect(() => {
        updateData();
    }, [updateData]);

    // Add gridContext - memoize to prevent unnecessary re-renders
    const gridContext = useMemo(() => ({
        isLocal: params?.systemParametersData?.data?.isLocal,
        campaignObj: params?.campaignObj,
        openActionMenuId: openActionMenuId,
        toggleActionMenu: toggleActionMenu,
        // Add any other context data needed by cell renderers
    }), [params?.systemParametersData?.data?.isLocal, params?.campaignObj, openActionMenuId, toggleActionMenu]);

    const isGroupOpenByDefault = useCallback((tableParams) => {
        const parentCampaign = params.locationState?.parentCampaign;
        return Boolean(
            parentCampaign &&
            Object.keys(parentCampaign).length > 0 &&
            params.locationState?.campaignObj?.id === tableParams.rowNode.data.id
        );
    }, [params.locationState?.parentCampaign, params.locationState?.campaignObj?.id]);

    const getRowClass = useCallback((params) => {
        if (params.data?.id === 'total-row') {
            return 'total-row';
        }
        if (params.data?.id === highlightedRowId) {
            return 'highlight-row';
        }
        return '';
    }, [highlightedRowId]);

    const onFirstDataRendered = useCallback(() => {
        const parentCampaign = params.locationState?.parentCampaign;
        const targetCampaignId = params.locationState?.campaignObj?.id;

        // Only need targetCampaignId to scroll - parentCampaign is optional (used for group expansion)
        if (!targetCampaignId) {
            return;
        }

        // Use setTimeout to allow the grid to fully render before scrolling
        setTimeout(() => {
            let targetNode = null;

            gridRef.current?.api?.forEachNode((node) => {
                if (node.data?.id === targetCampaignId) {
                    targetNode = node;
                    // If it's a group row with a parent campaign, ensure it's expanded first
                    if (node.group && !node.expanded && parentCampaign && Object.keys(parentCampaign).length > 0) {
                        node.setExpanded(true);
                    }
                }
            });

            // After finding the node, scroll to it
            if (targetNode) {
                // Wait for DOM to update after potential expansion
                setTimeout(() => {
                    // Search the entire document for the row
                    // Row IDs follow pattern: ${id}-chan-${random} or ${id}-pub-${random}
                    const allRows = document.querySelectorAll('[row-id]');
                    let rowDomElement = null;

                    for (const row of allRows) {
                        const rowId = row.getAttribute('row-id');
                        if (rowId && rowId.startsWith(`${targetCampaignId}-`)) {
                            rowDomElement = row;
                            break;
                        }
                    }

                    if (rowDomElement && !params?.mediaOrdersRenderRef?.current) {
                        rowDomElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        params.updateMediaOrdersRenderRef();
                    }
                    // Set the highlighted row ID for persistent styling via getRowClass
                    setHighlightedRowId(targetCampaignId);
                }, 300);
            }
        }, 100);
    }, [params.locationState?.parentCampaign, params.locationState?.campaignObj?.id]);

    return (
        <div className="media-orders-table">

            {isShowModal && editableData &&
                <InlineEditDialogBox
                    omniProfile={params.omniProfile}
                    taxonomySchemaArray={params.campaignTaxonomyData}
                    campaignObj={selectedCampaignObj}
                    taxonomyMap={null}
                    allTaxonomyData={null}
                    versionOneApi={params.versionOneApi}
                    systemParametersData={params.systemParametersData}
                    setSpinnerCount={params.setSpinnerCount}
                    displayInlineEditModal={isShowModal}
                    onInlineModalClose={closeModal}
                    taxonomyKeys={taxonomyKeys}
                    selectedRowData={editableData}
                    editFormData={editFormData}
                    isOverride={isInlineOverrideRef.current}
                    isCreatingChildCampaign={false}
                />
            }

            {data.length > 0 && <AgGridReact
                className="media-orders-aggrid-table"
                ref={gridRef}
                columnDefs={columnDefs}
                rowData={data}
                treeData={true}
                treeDataChildrenField={"nestedData"}
                groupDefaultExpanded={0}
                suppressContextMenu={true}
                autoGroupColumnDef={{
                    headerName: "Prisma Campaign",
                    field: "prismaCampaign",
                    cellRenderer: 'agGroupCellRenderer',
                    cellRendererParams: {
                        suppressCount: true,
                        innerRenderer: (params) => {

                            // Handle completely missing data
                            if (!params.data) {
                                return '';
                            }

                            // Handle valid data - return plain text only
                            const description = params.data.description || params.value || '';
                            const isChannel = !params.data.publisher_name;

                            const handleClick = () => {
                                handleDescriptionClick(params.data);
                            };

                            if (isChannel) {
                                return (
                                    <span
                                        onClick={handleClick}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {description}
                                    </span>
                                );
                            } else {
                                return (
                                    <span
                                        onClick={handleClick}
                                        style={{ cursor: 'pointer', paddingLeft: '16px' }}
                                    >
                                        {description}
                                    </span>
                                );
                            }
                        }
                    },
                    flex: 3
                }}
                getRowId={(params) => {
                    const rowId = `${params?.data?.id}-${params?.data?.publisher_name ? 'pub' : 'chan'}-${PlacementUtils.generateRandomId()}`;
                    return rowId;
                }}
                defaultColDef={defaultColDef}
                sideBar={false}
                context={gridContext}
                domLayout="autoHeight"
                isGroupOpenByDefault={isGroupOpenByDefault}
                onFirstDataRendered={onFirstDataRendered}
                getRowClass={getRowClass}
            />}
        </div>
    );
}

export default MediaOrdersTable;
export { defaultColDef, mediaOrdersColumnDefs };
